import type { CandidateDto } from '@pams/shared';
import { Loading, Skeleton } from '@/components/Skeleton';
import { OfficerAvatar } from '@/components/OfficerAvatar';
import { cn } from '@/lib/utils';
import { FILL_COPY } from './copy';
import { TAKEN_WASH } from './takenWash';

/**
 * One result in the search sheet.
 *
 * ⚠️ FIVE FIELDS, AND THEY ARE THE MITIGATION RATHER THAN DECORATION.
 * PA_REQUIREMENTS §6.2 asks for photo, name, rank, posting and phone on every row, because
 * picking the wrong Sen out of forty is the likeliest data-entry error in this system. A
 * list of bare names would be faster to render and slower to use correctly.
 */
export type OfficerResultRowProps = {
  /**
   * ⚠️ [C3c §4b] THE 90-DAY DUTY COUNT — `'pending'` UNTIL ITS OWN BATCH ANSWERS.
   *
   * Three states rather than two, because a number that has not arrived and a number that could
   * not be fetched are different facts: a skeleton says "coming", `—` says "not available". A
   * single `number | null` would draw one of them as the other for as long as the request took.
   */
  dutyCount: number | 'pending' | null;
  officer: CandidateDto;
  active: boolean;
  /**
   * ⚠️ [PRE-P6 §2b] DECIDED BY THE SHEET, NOT READ OFF THE DTO HERE.
   *
   * `officer.alreadyOnThisDuty` is the server's answer as of the last candidate fetch; the
   * sheet ORs it with what this screen has learned since, including writes still in flight.
   * Reading the DTO directly in this file would be a SECOND answer to one question, and the
   * one the operator sees would be the stale one.
   */
  alreadyOn: boolean;
};

export const OfficerResultRow = ({ officer, active, alreadyOn, dutyCount }: OfficerResultRowProps) => (
  <span
    data-already-named={alreadyOn || undefined}
    className={cn(
      'relative flex w-full items-center gap-3 rounded-md px-2 py-1.5 text-left',
      active && 'bg-accent text-accent-foreground',
      /*
       * ⚠️ [D4.5 §3c] SHOWN, NEVER REMOVED. An operator who cannot find somebody has to see WHY — a
       * name that silently vanishes from a search reads as a data problem and sends them to the
       * officer master looking for it.
       *
       * ⚠️ [amended C4a §5] A MUTED RED WASH, AND `opacity-60` IS GONE. The old comment claimed the
       * dimmed row's explanation "stays fully legible" and matched `SlotChip`'s read-only state; both
       * were false — CSS opacity fades every child, so the explanation measured about 2.4:1 in light,
       * and `SlotChip` carries no such class. The wash (`takenWash.ts`) leaves every word at full
       * contrast, and `isolate` + `relative` are what the overlay needs to land on THIS row.
       */
      alreadyOn && 'isolate',
      alreadyOn && TAKEN_WASH,
    )}
  >
    <OfficerAvatar name={officer.name} src={officer.photoUrl ?? undefined} className="size-9" />
    <span className="flex min-w-0 flex-1 flex-col">
      <span className="flex items-baseline gap-2">
        <span className="truncate text-sm font-medium">{officer.name}</span>
        <span className="shrink-0 text-xs text-muted-foreground">{officer.rankShortName ?? '—'}</span>
        {/*
          ⚠️ [C3c §4a] THE GL NUMBER, BESIDE THE RANK, AND ONLY WHEN THERE IS ONE.

          Rendered rather than dashed: this row already carries `—` twice, for the rank and the
          unit, and a third on the majority of rows is noise — unlike those two, an absent GL
          number is not a data gap anybody is chasing. `font-mono` because it is an identifier,
          which is the treatment `pa_number` already gets on the arrangements list.
        */}
        {officer.glNo ? (
          <span className="shrink-0 font-mono text-xs text-muted-foreground" data-testid="candidate-gl-no">
            {officer.glNo}
          </span>
        ) : null}
      </span>
      <span className="truncate text-xs text-muted-foreground">
        {officer.orgUnitName ?? '—'} · <span className="tabular-nums">{officer.phone}</span>
        {/*
          ⚠️ NOT A CLASH CHECK — FR-5.3.3 defers those entirely, and `conflictDeferred.test.ts`
          holds an exact EMPTY list of files allowed to mention that vocabulary. This is a
          UNIQUENESS check over rows the sheet already holds: `alreadyOnThisDuty` is computed
          per DEPLOYMENT, which is one duty point on one night. No query, no duty window, no
          other arrangement — so the same officer on a morning card and an evening card is
          untouched, which is legitimate and must stay possible.

          ⚠️ [D4.5 §3c] AND IT IS THE ROW'S OWN EXPLANATION — the word that travels with the wash, so
          colour is never the only signal ([C4a §5], `CoverageBar`'s greyscale rule). ⚠️ It stays in
          `muted-foreground`, NOT red: red text over the light wash measures under 4.5:1, which
          `takenTint.test.ts` pins as a negative control.
        */}
        {alreadyOn ? ` · ${FILL_COPY.alreadyNamed}` : ''}
      </span>
    </span>
    {/*
      ⚠️ [C3c §4b] THE 90-DAY DUTY COUNT, AT THE ROW'S TRAILING EDGE.

      ⚠️ NO STATUS COLOUR AND NO THRESHOLD. There is no "too many duties" rule in this product,
      and inventing a red one here would be yet another meaning of red on a screen that already
      carries fulfilment red-amber-green, attendance ([C3c §7]) and — on this very row — the
      muted "already taken" wash ([C4a §5]). The number is a fact for an operator to weigh, not a
      judgement the screen has made.

      ⚠️ AND THE ACCESSIBLE NAME IS THE WHOLE SENTENCE. A bare integer beside a name is
      unreadable to a screen reader — `CoverageBar`'s R2, that the decoration is hidden and the
      SENTENCE is the content.

      ⚠️ [amended C5 §11a] THE VISIBLE CHIP NOW READS `0 Recent PAs` RATHER THAN A BARE `0`, and
      the accessible sentence is UNCHANGED — it still says `duties … in the last 90 days`. Two
      audiences, two lengths, one number: the sentence keeps the precise noun and the window,
      which is the part an operator cannot infer from the figure; the chip keeps the short
      wording §11a asked for. The popover behind the hover is where the window is broken down.
    */}
    <span className="ml-auto shrink-0 text-xs text-muted-foreground" data-testid="candidate-duty-count">
      {dutyCount === 'pending' ? (
        <Loading label={FILL_COPY.dutyCountLoading}>
          <Skeleton className="h-3 w-6" />
        </Loading>
      ) : dutyCount === null ? (
        <span aria-hidden>—</span>
      ) : (
        <span className="tabular-nums" aria-label={FILL_COPY.dutyCountLabel(dutyCount)}>
          {FILL_COPY.recentPas(dutyCount)}
        </span>
      )}
    </span>
  </span>
);
